/**
 * 입력 컨트롤러 (FR-002/003, research §8).
 * Pointer Events로 마우스/터치 통합: raycast picking → 카메라 평행 평면에서 오브젝트 추종
 * (kinematic) → 릴리스 속도 추정 → 던지기 임펄스. 모바일(터치)에서는 궤적 보조선 표시.
 * 약하게 놓으면 제자리 드롭(게이지 미소모, Assumptions).
 */
import * as THREE from 'three';
import type { EventBus } from '../core/EventBus';
import type { PhysicsWorld } from '../physics/PhysicsWorld';
import type { ObjectSpawner, SpawnedObject } from '../gameplay/ObjectSpawner';
import type { TrajectoryGuide } from './trajectory';
import { BALANCE } from '../config/balance';
import { LAYOUT } from '../config/layout';
import { boundingBoxOf } from '../physics/fracture';

const MIN_THROW_SPEED = 1.5; // 이하이면 제자리 드롭(던지기 아님)
const SAMPLE_MS = 90; // 릴리스 속도 추정 윈도우

// 잡은(kinematic) 물건의 드래그 한계 — 정적 콜라이더 안쪽 면(LAYOUT과 동기).
const FLOOR_TOP_Y = LAYOUT.floor.y + LAYOUT.floor.size[1] / 2;
const CEIL_BOTTOM_Y = LAYOUT.ceiling.y - LAYOUT.ceiling.size[1] / 2;
const WALL_INNER_X = LAYOUT.sideWalls.x - LAYOUT.sideWalls.size[0] / 2;
const HOVER_CHECK_MS = 40; // 호버 레이캐스트 스로틀(포인터 이벤트 스팸 방지)
const HOVER_SHELL_SCALE = 1.05; // 하이라이트 셸 확대율(외곽선 두께)

// 브레이커블 전용 호버 하이라이트 셸 머티리얼 — 장식 소품(deskProps)과 달리 "부술 수 있음"을
// 알리는 시각 신호. BackSide+확대로 실루엣을 따라가는 옐로 외곽 글로우를 만든다.
// 오브젝트 머티리얼은 캐시 공유라 직접 emissive를 만지면 전 인스턴스가 발광하므로 셸로 분리.
const HOVER_MAT = new THREE.MeshBasicMaterial({
  color: 0xffd43b,
  transparent: true,
  opacity: 0.28,
  blending: THREE.AdditiveBlending,
  side: THREE.BackSide,
  depthWrite: false,
});

interface Sample {
  pos: THREE.Vector3;
  t: number;
}

export class InputController {
  private raycaster = new THREE.Raycaster();
  private ndc = new THREE.Vector2();
  private dragPlane = new THREE.Plane();
  private planePoint = new THREE.Vector3();
  private held: SpawnedObject | null = null;
  /** 그랩 순간의 (물건 중심 − 클릭 지점) 오프셋 — 잡을 때 물건이 포인터로 점프하지 않게 유지. */
  private grabOffset = new THREE.Vector3();
  private samples: Sample[] = [];
  private isTouch = false;
  private hoverId: number | null = null;
  /** 호버 대상 mesh의 자식으로 붙는 하이라이트 셸(물리 동기화를 공짜로 따라감). */
  private hoverShell: THREE.Group | null = null;
  private lastHoverCheck = 0;
  /** EventBus 구독 해제 핸들(dispose용). */
  private offBus: Array<() => void>;

  /**
   * 수명 싱글턴: 앱 전체에서 1회 생성되며 다시하기(replay)도 같은 인스턴스를 재사용하므로
   * window/canvas 리스너가 누적되지 않는다. 만약 재생성이 필요해지면 반드시 dispose()로
   * 리스너·구독을 정리할 것.
   */
  constructor(
    private canvas: HTMLCanvasElement,
    private camera: THREE.PerspectiveCamera,
    private spawner: ObjectSpawner,
    private physics: PhysicsWorld,
    private bus: EventBus,
    private trajectory: TrajectoryGuide,
    private isInteractive: () => boolean,
  ) {
    canvas.addEventListener('pointerdown', this.onDown);
    canvas.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
    // 호버 중이던 오브젝트가 부서지면 하이라이트 셸 정리(마우스가 안 움직여도).
    const offBroken = this.bus.on('object:broken', (e) => {
      if (e.instanceId === this.hoverId) this.setHover(null);
      // 잡고 있던 물건이 (다른 물건을 내리쳐) 깨지면 잡기 해제 — 제거된 body 참조 방지.
      if (this.held && e.instanceId === this.held.instanceId) {
        this.held = null;
        this.trajectory.hide();
      }
    });
    // 세션 시작/리셋은 spawner.resetAll()이 잡은 바디까지 제거·재생성한다 — 이전 세션 객체를
    // 계속 쥐고 있으면 이후 onMove/onUp이 제거된 body를 조작하므로(핸들 재사용 시 엉뚱한
    // 바디에 kinematic 이동, 아니면 WASM 트랩) held/hover 스테일 참조를 즉시 해제한다.
    const releaseStale = (): void => {
      this.held = null;
      this.trajectory.hide();
      this.setHover(null);
      this.canvas.classList.remove('grabbing');
    };
    this.offBus = [
      offBroken,
      this.bus.on('session:start', releaseStale),
      this.bus.on('session:reset', releaseStale),
    ];
  }

  /** 리스너·구독 정리(수명 싱글턴이라 평소엔 호출하지 않음 — 재생성 대비 API). */
  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onDown);
    this.canvas.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    for (const off of this.offBus) off();
    this.setHover(null);
  }

  private setNdc(e: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  /** 현재 ndc 기준 레이캐스트로 브레이커블 오브젝트 피킹(다중 파트 Group → 소유자 역추적). */
  private pickObject(): { obj: SpawnedObject; point: THREE.Vector3 } | null {
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const meshes: THREE.Object3D[] = [];
    for (const o of this.spawner.objects.values()) {
      if (o.state !== 'broken') meshes.push(o.mesh);
    }
    const hit = this.raycaster.intersectObjects(meshes, true)[0];
    if (!hit) return null;
    let node: THREE.Object3D | null = hit.object;
    while (node && node.userData.instanceId === undefined) node = node.parent;
    const instanceId = node?.userData.instanceId as number | undefined;
    if (instanceId === undefined) return null;
    const obj = this.spawner.objects.get(instanceId);
    return obj ? { obj, point: hit.point } : null;
  }

  /**
   * 호버 하이라이트 전환: 이전 셸 제거 후 대상 mesh 지오메트리를 공유하는 셸을 자식으로 부착.
   * 지오메트리는 모델 캐시와 공유되므로 dispose하지 않는다.
   */
  private setHover(obj: SpawnedObject | null): void {
    const id = obj?.instanceId ?? null;
    if (id === this.hoverId) return;
    if (this.hoverShell) {
      this.hoverShell.removeFromParent();
      this.hoverShell = null;
    }
    this.hoverId = id;
    if (!obj) return;

    const sources: THREE.Mesh[] = [];
    obj.mesh.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) sources.push(o as THREE.Mesh);
    });
    const shell = new THREE.Group();
    for (const src of sources) {
      const m = new THREE.Mesh(src.geometry, HOVER_MAT);
      m.position.copy(src.position);
      m.quaternion.copy(src.quaternion);
      m.scale.copy(src.scale);
      m.castShadow = false;
      m.receiveShadow = false;
      shell.add(m);
    }
    shell.scale.setScalar(HOVER_SHELL_SCALE);
    obj.mesh.add(shell);
    this.hoverShell = shell;
  }

  /** 드래그 중이 아닐 때의 포인터 이동 — 브레이커블 위면 하이라이트(스로틀 레이캐스트). */
  private updateHover(e: PointerEvent): void {
    if (!this.isInteractive()) {
      this.setHover(null);
      return;
    }
    const now = performance.now();
    if (now - this.lastHoverCheck < HOVER_CHECK_MS) return;
    this.lastHoverCheck = now;
    this.setNdc(e);
    this.setHover(this.pickObject()?.obj ?? null);
  }

  private onDown = (e: PointerEvent): void => {
    if (!this.isInteractive()) return;
    // 멀티터치 가드: 이미 잡은 물건이 있는데 두 번째 포인터가 다른 물건을 집으면 첫 물건이
    // kinematic 'grabbed' 고아(공중 정지)로 남는다 — 첫 잡기가 끝날 때까지 추가 잡기 무시.
    if (this.held) return;
    this.isTouch = e.pointerType === 'touch';
    // 좌클릭을 누르는 동안 집는 손(grabbing) 커서.
    this.canvas.classList.add('grabbing');
    this.setNdc(e);
    const picked = this.pickObject();
    if (!picked) return;
    const { obj, point } = picked;
    this.setHover(null); // 잡기 순간 하이라이트 해제

    this.held = obj;
    obj.state = 'grabbed';
    // 카메라를 향하는 평면(그랩 지점 통과)에서 드래그.
    this.camera.getWorldDirection(this.dragPlane.normal);
    this.dragPlane.setFromNormalAndCoplanarPoint(this.dragPlane.normal, point);

    // 바디를 kinematic으로 전환해 포인터 추종.
    obj.body.setBodyType(this.physics.RAPIER.RigidBodyType.KinematicPositionBased, true);
    obj.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    obj.body.setAngvel({ x: 0, y: 0, z: 0 }, true);

    // 그랩 오프셋(중심 − 클릭점) 기록 — 잡는 순간 중심이 클릭 지점으로 점프하지 않게.
    const c = obj.body.translation();
    this.grabOffset.set(c.x - point.x, c.y - point.y, c.z - point.z);

    this.samples = [{ pos: point.clone(), t: performance.now() }];
    this.bus.emit('input:grab', { instanceId: obj.instanceId });
    this.canvas.setPointerCapture?.(e.pointerId);
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.held) {
      this.updateHover(e);
      return;
    }
    this.setNdc(e);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    if (!this.raycaster.ray.intersectPlane(this.dragPlane, this.planePoint)) return;

    // 그랩 오프셋 적용 — 잡는 순간 물건이 클릭 지점으로 점프하지 않게(점프하면 아래로 당길 때
    // 위에서 내리꽂은 것처럼 파괴됐음). 물건 중심 목표 = 포인터 평면점 + 오프셋.
    let tx = this.planePoint.x + this.grabOffset.x;
    let ty = this.planePoint.y + this.grabOffset.y;
    const tz = this.planePoint.z + this.grabOffset.z;
    // kinematic 바디는 정적 콜라이더를 무시하고 관통하므로 물리로는 못 막아 여기서 직접 클램프.
    // - 하한: 책상 XZ 안이면 상판, 밖(책상 앞/옆)이면 바닥 — 살살 내리면 얹히고, 세게 내리쳐야
    //   파괴된다(DestructionSystem.checkGrabbedDeskSmash). 클램프 없이는 얇은 바닥/천장을 뚫은
    //   채 릴리스돼 방 밖으로 영영 사라졌다(앞쪽에서 위/아래 스매시 시 물건 소실 버그).
    // - 상한: 천장 아랫면. 좌우: 옆벽 안쪽 면.
    const bb = boundingBoxOf(this.held.def.primitive);
    const halfW = LAYOUT.desk.size[0] / 2;
    const halfD = LAYOUT.desk.size[2] / 2;
    const overDesk = Math.abs(tx) <= halfW && Math.abs(tz) <= halfD;
    const minY = (overDesk ? LAYOUT.desk.topY : FLOOR_TOP_Y) + bb[1] / 2;
    const maxY = CEIL_BOTTOM_Y - bb[1] / 2;
    ty = Math.min(Math.max(ty, minY), maxY);
    const maxX = WALL_INNER_X - bb[0] / 2;
    tx = Math.min(Math.max(tx, -maxX), maxX);
    this.held.body.setNextKinematicTranslation({ x: tx, y: ty, z: tz });

    const now = performance.now();
    this.samples.push({ pos: this.planePoint.clone(), t: now });
    // 오래된 샘플 제거.
    while (this.samples.length > 2 && now - this.samples[0].t > SAMPLE_MS) this.samples.shift();

    if (this.isTouch) {
      // 미리보기 궤적은 실제 발사 속도와 동일 변환을 거쳐야 오차가 없다.
      this.trajectory.show(this.planePoint, throwVelocity(this.estimateVelocity()));
    }
  };

  private onUp = (): void => {
    // 좌클릭을 떼면 손을 펼친(grab) 커서로 복귀 — 물건을 잡고 있지 않아도 항상 복원.
    this.canvas.classList.remove('grabbing');
    if (!this.held) return;
    const obj = this.held;
    this.held = null;
    this.trajectory.hide();

    const vel = this.estimateVelocity();
    const speed = vel.length();

    // 바디를 다시 dynamic으로.
    obj.body.setBodyType(this.physics.RAPIER.RigidBodyType.Dynamic, true);

    if (speed >= MIN_THROW_SPEED) {
      const v = throwVelocity(vel);
      obj.body.setLinvel({ x: v.x, y: v.y, z: v.z }, true);
      obj.state = 'thrown';
      this.bus.emit('input:throw', {
        instanceId: obj.instanceId,
        velocity: { x: v.x, y: v.y, z: v.z },
      });
    } else {
      // 제자리 드롭 — 게이지 미소모.
      obj.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      obj.state = 'intact';
    }
  };

  private estimateVelocity(): THREE.Vector3 {
    if (this.samples.length < 2) return new THREE.Vector3();
    const last = this.samples[this.samples.length - 1];
    const first = this.samples[0];
    const dt = Math.max(0.016, (last.t - first.t) / 1000);
    return last.pos.clone().sub(first.pos).divideScalar(dt);
  }
}

/**
 * 릴리스 속도 → 실제 발사 속도(순수). 릴리스 시점과 모바일 미리보기 궤적이 같은 값을
 * 쓰도록 한 곳에 모은다. 상향 성분이 큰 스와이프는 전방(−z) 45° 위 아크로 재구성하고,
 * 좌우(x) 조준은 약하게 유지한 뒤 임펄스 스케일을 곱한다. 입력 벡터는 변형하지 않는다.
 */
function throwVelocity(vel: THREE.Vector3): THREE.Vector3 {
  const speed = vel.length();
  const out = vel.clone();
  if (out.y > MIN_THROW_SPEED * 0.6) {
    out.copy(new THREE.Vector3(out.x * 0.25, 1, -1).normalize().multiplyScalar(speed));
  }
  return out.multiplyScalar(BALANCE.throwImpulseScale);
}
